<template>
  <div class="admin-container">
    <!-- Login View -->
    <div v-if="!isLoggedIn" class="login-box">
      <h2>管理后台</h2>
      <input v-model="username" placeholder="用户名" />
      <input v-model="password" type="password" placeholder="密码" @keyup.enter="login" />
      <button @click="login">登录</button>
      <p v-if="error" class="error">{{ error }}</p>
    </div>

    <!-- Admin Panel -->
    <div v-else class="admin-panel">
      <!-- Sidebar -->
      <div class="sidebar">
        <h3>📺 管理后台</h3>
        <div class="menu-item" :class="{active: tab === 'dashboard'}" @click="tab='dashboard'; fetchSiteStats()">📊 仪表盘</div>
        <div class="menu-item" :class="{active: tab === 'videos'}" @click="tab='videos'">🎬 视频管理</div>
        <div class="menu-item" :class="{active: tab === 'users'}" @click="tab='users'">👤 用户管理</div>
        <div class="menu-item" :class="{active: tab === 'payments'}" @click="tab='payments'; fetchPayments()">💰 支付记录</div>
        <div class="menu-item" :class="{active: tab === 'pricing'}" @click="tab='pricing'; fetchPricing()">💎 价格管理</div>
        <div class="menu-item" :class="{active: tab === 'promoters'}" @click="tab='promoters'; fetchPromoters()">📢 推广管理</div>
        <div class="menu-item" :class="{active: tab === 'settings'}" @click="tab='settings'; fetchSettings()">⚙️ 网站设置</div>
        <div class="menu-item" :class="{active: tab === 'system'}" @click="tab='system'; fetchVersionInfo()">ℹ️ 系统信息</div>
        <div class="menu-item logout" @click="logout">🚪 退出</div>
      </div>

      <!-- Content -->
      <div class="content">
        <!-- Dashboard -->
        <div v-if="tab === 'dashboard'" class="section">
          <h2>仪表盘</h2>
          <div class="stat-cards">
            <div class="stat-card"><div class="stat-num">{{ stats.videos }}</div><div>视频数量</div></div>
            <div class="stat-card"><div class="stat-num">{{ stats.users }}</div><div>用户数量</div></div>
            <div class="stat-card"><div class="stat-num">{{ stats.categories }}</div><div>分类数量</div></div>
            <div class="stat-card"><div class="stat-num">{{ stats.payments }}</div><div>订单数量</div></div>
            <div class="stat-card revenue"><div class="stat-num">¥{{ stats.revenue }}</div><div>总收入</div></div>
          </div>

          <!-- Site Statistics Section -->
          <div class="site-stats-section">
            <h3>📈 网站访问统计</h3>
            
            <!-- Summary Cards -->
            <div class="stats-grid">
              <div class="stat-box">
                <div class="stat-value">{{ siteStats.totalVisits }}</div>
                <div class="stat-label">总访问量</div>
              </div>
              <div class="stat-box">
                <div class="stat-value">{{ siteStats.uniqueIps }}</div>
                <div class="stat-label">总独立IP</div>
              </div>
              <div class="stat-box highlight">
                <div class="stat-value">{{ siteStats.todayVisits }}</div>
                <div class="stat-label">今日访问</div>
              </div>
              <div class="stat-box highlight-green">
                <div class="stat-value">{{ siteStats.todayUniqueIps }}</div>
                <div class="stat-label">今日独立IP</div>
              </div>
            </div>

            <!-- Device Breakdown -->
            <div class="stats-detail-section" v-if="siteStats.deviceStats && siteStats.deviceStats.length > 0">
              <h4>📱 设备分布</h4>
              <div class="device-stats">
                <div v-for="device in siteStats.deviceStats" :key="device.deviceType" class="device-item">
                  <span class="device-icon">
                    {{ device.deviceType === 'desktop' ? '💻' : (device.deviceType === 'mobile' ? '📱' : (device.deviceType === 'tablet' ? '📲' : '❓')) }}
                  </span>
                  <span class="device-name">{{ device.deviceType === 'desktop' ? '桌面端' : (device.deviceType === 'mobile' ? '移动端' : (device.deviceType === 'tablet' ? '平板' : '未知')) }}</span>
                  <span class="device-count">{{ device.count }}</span>
                </div>
              </div>
            </div>

            <!-- Daily Visits -->
            <div class="stats-detail-section" v-if="siteStats.dailyVisits && siteStats.dailyVisits.length > 0">
              <h4>📅 最近7天趋势</h4>
              <table class="mini-table">
                <thead>
                  <tr>
                    <th>日期</th>
                    <th>访问量</th>
                    <th>独立IP</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="day in siteStats.dailyVisits" :key="day.date">
                    <td>{{ day.date }}</td>
                    <td>{{ day.visits }}</td>
                    <td>{{ day.uniqueIps }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Two Column Layout for Top Tables -->
            <div class="top-tables-row">
              <!-- Top Referrers -->
              <div class="stats-detail-section" v-if="siteStats.topReferrers && siteStats.topReferrers.length > 0">
                <h4>🔗 热门来源</h4>
                <table class="mini-table">
                  <thead>
                    <tr>
                      <th>来源</th>
                      <th>次数</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="ref in siteStats.topReferrers" :key="ref.referer">
                      <td class="url-cell">{{ ref.referer }}</td>
                      <td>{{ ref.count }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Top Pages -->
              <div class="stats-detail-section" v-if="siteStats.topPages && siteStats.topPages.length > 0">
                <h4>📄 热门页面</h4>
                <table class="mini-table">
                  <thead>
                    <tr>
                      <th>页面</th>
                      <th>次数</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="page in siteStats.topPages" :key="page.path">
                      <td class="url-cell">{{ page.path }}</td>
                      <td>{{ page.count }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <button class="btn-sm" @click="fetchSiteStats()" style="margin-top: 15px;">🔄 刷新统计</button>
          </div>
        </div>

        <!-- Videos -->
        <div v-if="tab === 'videos'" class="section">
          <h2>视频管理</h2>
          <div class="toolbar">
            <input v-model="videoSearch" placeholder="搜索标题..." @input="fetchVideos" />
            <select v-model="videoCategory" @change="fetchVideos">
              <option value="">全部分类</option>
              <option v-for="cat in categories" :key="cat" :value="cat">{{ cat }}</option>
            </select>
            <button class="btn-danger" @click="batchDelete" :disabled="selectedVideos.length === 0">
              删除选中 ({{ selectedVideos.length }})
            </button>
          </div>
          <table>
            <thead>
              <tr>
                <th><input type="checkbox" @change="toggleAll" /></th>
                <th>ID</th>
                <th>封面</th>
                <th>标题</th>
                <th>分类</th>
                <th>播放量</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="video in videos" :key="video.id">
                <td><input type="checkbox" v-model="selectedVideos" :value="video.id" /></td>
                <td>{{ video.id }}</td>
                <td><img :src="getCoverUrl(video.coverUrl)" class="cover-thumb" /></td>
                <td class="title-cell">{{ video.title }}</td>
                <td>{{ video.category }}</td>
                <td>{{ video.views }}</td>
                <td>
                  <button class="btn-sm" @click="editVideo(video)">编辑</button>
                  <button class="btn-sm btn-danger" @click="deleteVideo(video.id)">删除</button>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="pagination">
            <button @click="videoPage--" :disabled="videoPage <= 1">上一页</button>
            <span>{{ videoPage }} / {{ videoPages }}</span>
            <button @click="videoPage++" :disabled="videoPage >= videoPages">下一页</button>
          </div>
        </div>

        <!-- Users -->
        <div v-if="tab === 'users'" class="section">
          <h2>用户管理</h2>
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>用户名</th>
                <th>会员到期</th>
                <th>金币</th>
                <th>注册时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="user in users" :key="user.id">
                <td>{{ user.id }}</td>
                <td>{{ user.username }}</td>
                <td>{{ formatDateTime(user.memberDate) || '非会员' }}</td>
                <td>{{ user.coins }}</td>
                <td>{{ formatDateTime(user.createdAt) }}</td>
                <td>
                  <button class="btn-sm" @click="editUser(user)">编辑</button>
                  <button class="btn-sm btn-danger" @click="deleteUser(user.id)">删除</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Payments -->
        <div v-if="tab === 'payments'" class="section">
          <h2>支付记录</h2>
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>用户</th>
                <th>类型</th>
                <th>金额</th>
                <th>状态</th>
                <th>视频</th>
                <th>创建时间</th>
                <th>支付时间</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in payments" :key="p.id">
                <td>{{ p.id }}</td>
                <td>{{ p.username }}</td>
                <td>{{ getPaymentType(p.type) }}</td>
                <td class="amount">¥{{ p.amount }}</td>
                <td><span :class="'status-' + p.status">{{ p.status }}</span></td>
                <td class="title-cell">{{ p.videoTitle || '-' }}</td>
                <td>{{ formatDateTime(p.createdAt) }}</td>
                <td>{{ p.paidAt ? formatDateTime(p.paidAt) : '-' }}</td>
              </tr>
            </tbody>
          </table>
          <div class="pagination">
            <button @click="paymentPage--; fetchPayments()" :disabled="paymentPage <= 1">上一页</button>
            <span>{{ paymentPage }} / {{ paymentPages }}</span>
            <button @click="paymentPage++; fetchPayments()" :disabled="paymentPage >= paymentPages">下一页</button>
          </div>
        </div>

        <!-- Pricing Management -->
        <div v-if="tab === 'pricing'" class="section">
          <h2>💎 价格管理</h2>
          <p class="pricing-tip">管理各种套餐的价格配置，修改后实时生效。会员套餐期间所有影片免费观看。</p>
          <table>
            <thead>
              <tr>
                <th>套餐类型</th>
                <th>名称</th>
                <th>价格 (¥)</th>
                <th>时长</th>
                <th>类型</th>
                <th>启用</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in pricingConfigs" :key="item.id" :class="{'disabled-row': !item.enabled}">
                <td><span class="type-badge">{{ item.type }}</span></td>
                <td>{{ item.name }}</td>
                <td>
                  <input type="number" v-model.number="item.price" class="price-input" min="0" step="0.01" />
                </td>
                <td>{{ item.duration }}</td>
                <td>
                  <span :class="item.isMembership ? 'badge-member' : 'badge-single'">
                    {{ item.isMembership ? '会员' : '单片' }}
                  </span>
                </td>
                <td>
                  <label class="switch">
                    <input type="checkbox" v-model="item.enabled" />
                    <span class="slider"></span>
                  </label>
                </td>
                <td>
                  <button class="btn-sm btn-primary" @click="savePricing(item)">保存</button>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="pricing-actions">
            <button class="btn-primary" @click="saveAllPricing">💾 保存全部修改</button>
          </div>
        </div>

        <!-- Promoters Management -->
        <div v-if="tab === 'promoters'" class="section">
          <h2>📢 推广管理</h2>
          
          <!-- Add Promoter Form -->
          <div class="add-promoter-form">
            <h4>添加推广人员</h4>
            <div class="form-row">
              <input v-model="newPromoter.promoterId" placeholder="推广ID（唯一标识）" />
              <input v-model="newPromoter.name" placeholder="推广人员名称" />
              <input v-model.number="newPromoter.commissionRate" type="number" placeholder="分润比例%" min="0" max="100" />
            </div>
            <div class="form-row" style="margin-top: 10px;">
              <input v-model="newPromoter.username" placeholder="登录账号" />
              <input v-model="newPromoter.password" type="password" placeholder="登录密码" />
              <input v-model="newPromoter.customDomain" placeholder="专属域名（可选）" />
              <button class="btn-primary" @click="addPromoter">➕ 添加</button>
            </div>
          </div>

          <!-- Promoters Table -->
          <table>
            <thead>
              <tr>
                <th>推广ID</th>
                <th>名称</th>
                <th>登录账号</th>
                <th>专属域名</th>
                <th>分润比例</th>
                <th>启用</th>
                <th>推广链接</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in promoters" :key="item.id" :class="{'disabled-row': !item.enabled}">
                <td><span class="type-badge">{{ item.promoterId }}</span></td>
                <td>{{ item.name }}</td>
                <td>{{ item.username || '-' }}</td>
                <td>{{ item.customDomain || '-' }}</td>
                <td>{{ item.commissionRate }}%</td>
                <td>
                  <label class="switch">
                    <input type="checkbox" v-model="item.enabled" @change="updatePromoter(item)" />
                    <span class="slider"></span>
                  </label>
                </td>
                <td>
                  <button class="btn-sm" @click="copyPromoLink(item.promoterId)">📋 复制链接</button>
                </td>
                <td>
                  <button class="btn-sm" @click="viewPromoterStats(item.promoterId)">📊 统计</button>
                  <button class="btn-sm btn-danger" @click="deletePromoter(item.id)">删除</button>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Stats Modal -->
          <div v-if="showPromoterStats" class="stats-panel">
            <h4>📊 推广统计 - {{ currentPromoterStats.promoterId }}</h4>
            <div class="stats-grid">
              <div class="stat-box">
                <div class="stat-value">{{ currentPromoterStats.totalVisits }}</div>
                <div class="stat-label">访问量</div>
              </div>
              <div class="stat-box">
                <div class="stat-value">{{ currentPromoterStats.uniqueIps }}</div>
                <div class="stat-label">独立IP</div>
              </div>
              <div class="stat-box">
                <div class="stat-value">{{ currentPromoterStats.registeredUsers }}</div>
                <div class="stat-label">注册会员</div>
              </div>
              <div class="stat-box">
                <div class="stat-value">{{ currentPromoterStats.totalPayments }}</div>
                <div class="stat-label">购买次数</div>
              </div>
              <div class="stat-box highlight">
                <div class="stat-value">¥{{ currentPromoterStats.totalAmount }}</div>
                <div class="stat-label">购买金额</div>
              </div>
              <div class="stat-box highlight-green">
                <div class="stat-value">¥{{ currentPromoterStats.commissionAmount }}</div>
                <div class="stat-label">分润金额 ({{ currentPromoterStats.commissionRate }}%)</div>
              </div>
            </div>
            <button class="btn-sm" @click="showPromoterStats = false">关闭</button>
          </div>

        </div>

        <!-- Site Settings -->
        <div v-if="tab === 'settings'" class="section">
          <h2>⚙️ 网站设置</h2>
          
          <div class="settings-form">
            <div class="setting-item">
              <label>网站名称</label>
              <input v-model="siteSettings.siteName" placeholder="网站名称" />
            </div>
            <div class="setting-item">
              <label>网站描述</label>
              <input v-model="siteSettings.siteDescription" placeholder="网站描述" />
            </div>
            <div class="setting-item">
              <label>联系方式</label>
              <input v-model="siteSettings.contactInfo" placeholder="联系方式" />
            </div>
            
            <h3 style="margin-top: 30px; margin-bottom: 20px; color: #e94560;">💳 支付设置 (Fiuu / Alipay)</h3>
            
            <div class="setting-item">
              <label>Fiuu Merchant ID (商户ID)</label>
              <input v-model="siteSettings.fiuuMerchantId" placeholder="Merchant ID" />
            </div>
            <div class="setting-item">
              <label>Fiuu Verify Key (验证密钥)</label>
              <input v-model="siteSettings.fiuuVerifyKey" type="password" placeholder="Verify Key" />
            </div>
            <div class="setting-item">
              <label>Fiuu Secret Key (密钥)</label>
              <input v-model="siteSettings.fiuuSecretKey" type="password" placeholder="Secret Key" />
            </div>
            <div class="setting-item">
              <label>模式设置</label>
              <div style="display: flex; align-items: center; gap: 10px;">
                <label class="switch">
                  <input type="checkbox" v-model="siteSettings.fiuuSandbox" true-value="true" false-value="false" />
                  <span class="slider"></span>
                </label>
                <span>{{ siteSettings.fiuuSandbox === 'true' ? 'Sandbox (测试环境)' : 'Production (生产环境)' }}</span>
              </div>
            </div>
            
            <h3 style="margin-top: 30px; margin-bottom: 20px; color: #4CAF50;">🌐 域名配置</h3>
            
            <div class="setting-item">
              <label>前端域名 (ReturnURL用)</label>
              <input v-model="siteSettings.frontendDomain" placeholder="例如: https://your-domain.com" />
              <small style="color: #888; font-size: 12px; margin-top: 5px; display: block;">
                支付完成后跳转地址前缀，留空则使用默认 http://localhost:8080
              </small>
            </div>
            <div class="setting-item">
              <label>后端域名 (回调通知用)</label>
              <input v-model="siteSettings.backendDomain" placeholder="例如: https://api.your-domain.com" />
              <small style="color: #888; font-size: 12px; margin-top: 5px; display: block;">
                Fiuu回调通知地址前缀，留空则使用默认 http://localhost:3000
              </small>
            </div>

            <div class="settings-actions">
              <button class="btn-primary" @click="saveSettings">💾 保存设置</button>
            </div>
          </div>
          
          <div class="settings-tip">
            <p>💡 修改网站名称后，需要刷新前端页面才能看到效果。</p>
          </div>
        </div>

        <!-- Crawler -->
        <div v-if="tab === 'crawler'" class="section">
          <h2>采集控制</h2>
          <div class="crawler-controls">
            <div class="control-row">
              <label>每分类采集数量:</label>
              <input type="number" v-model="crawlerConfig.videosPerCategory" min="1" max="50" />
            </div>
            <div class="control-row">
              <button class="btn-primary" @click="startCrawler" :disabled="crawlerStatus.running">🚀 开始采集</button>
              <button class="btn-danger" @click="stopCrawler" :disabled="!crawlerStatus.running">⏹️ 停止采集</button>
            </div>
            <div class="status-bar">
              <span :class="{'status-running': crawlerStatus.running}">
                {{ crawlerStatus.running ? '🟢 运行中' : '🔴 已停止' }}
              </span>
              <span>进度: {{ crawlerStatus.progress || '-' }}</span>
            </div>
          </div>
          <div class="log-box">
            <h4>日志输出</h4>
            <pre ref="logBox">{{ crawlerStatus.logs.join('') }}</pre>
          </div>
        </div>

        <!-- System Info -->
        <div v-if="tab === 'system'" class="section">
          <h2>ℹ️ 系统信息</h2>
          
          <div class="settings-form">
            <h3 style="margin-bottom: 20px; color: #4CAF50;">📦 版本信息</h3>
            <div class="version-info">
              <div class="version-item">
                <span class="version-label">前端版本:</span>
                <span class="version-value">{{ versionInfo.frontend }}</span>
              </div>
              <div class="version-item">
                <span class="version-label">后端版本:</span>
                <span class="version-value">{{ versionInfo.backend || '加载中...' }}</span>
              </div>
            </div>
            
            <h3 style="margin-top: 40px; margin-bottom: 20px; color: #e94560;">🔐 修改管理员密码</h3>
            <div class="setting-item">
              <label>当前密码</label>
              <input v-model="passwordForm.currentPassword" type="password" placeholder="输入当前密码" />
            </div>
            <div class="setting-item">
              <label>新密码</label>
              <input v-model="passwordForm.newPassword" type="password" placeholder="输入新密码（至少6位）" />
            </div>
            <div class="setting-item">
              <label>确认新密码</label>
              <input v-model="passwordForm.confirmPassword" type="password" placeholder="再次输入新密码" />
            </div>
            <div class="settings-actions">
              <button class="btn-primary" @click="changeAdminPassword">🔑 修改密码</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Modal -->
    <div v-if="showModal" class="modal-mask" @click="showModal=false">
      <div class="modal-box" @click.stop>
        <h3>{{ editType === 'video' ? '编辑视频' : '编辑用户' }}</h3>
        <div v-if="editType === 'video'">
          <label>标题</label>
          <input v-model="editItem.title" />
          <label>分类</label>
          <input v-model="editItem.category" />
          <label>视频URL</label>
          <textarea v-model="editItem.videoUrl" rows="2"></textarea>
          <label>封面URL</label>
          <input v-model="editItem.coverUrl" />
          <div v-if="editItem.coverUrl" class="cover-preview">
            <img :src="getCoverUrl(editItem.coverUrl)" />
          </div>
        </div>
        <div v-if="editType === 'user'">
          <label>会员到期日期</label>
          <input type="date" v-model="editItem.memberDate" />
          <label>金币</label>
          <input type="number" v-model="editItem.coins" />
        </div>
        <div class="modal-actions">
          <button @click="showModal=false">取消</button>
          <button class="btn-primary" @click="saveEdit">保存</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Admin',
  data() {
    return {
      isLoggedIn: false,
      username: '',
      password: '',
      error: '',
      tab: 'dashboard',
      stats: { videos: 0, users: 0, categories: 0, payments: 0, revenue: 0 },
      videos: [],
      videoPage: 1,
      videoPages: 1,
      videoSearch: '',
      videoCategory: '',
      categories: [],
      selectedVideos: [],
      users: [],
      payments: [],
      paymentPage: 1,
      paymentPages: 1,
      crawlerConfig: { videosPerCategory: 1 },
      crawlerStatus: { running: false, logs: [], progress: '' },
      crawlerInterval: null,
      pricingConfigs: [],
      promoters: [],
      newPromoter: { promoterId: '', name: '', commissionRate: 10, username: '', password: '', customDomain: '' },
      showPromoterStats: false,
      currentPromoterStats: {},
      siteSettings: { 
        siteName: '', 
        siteDescription: '', 
        contactInfo: '',
        fiuuMerchantId: '',
        fiuuVerifyKey: '',
        fiuuSecretKey: '',
        fiuuSandbox: 'true',
        frontendDomain: '',
        backendDomain: ''
      },
      showModal: false,
      editType: '',
      editItem: {},
      // System Info
      versionInfo: { frontend: '1.0.1', backend: '' },
      passwordForm: { currentPassword: '', newPassword: '', confirmPassword: '' },
      // Site Statistics
      siteStats: {
        totalVisits: 0,
        uniqueIps: 0,
        todayVisits: 0,
        todayUniqueIps: 0,
        deviceStats: [],
        dailyVisits: [],
        topReferrers: [],
        topPages: []
      }
    }
  },
  mounted() {
    const token = localStorage.getItem('adminToken');
    if (token) {
      this.isLoggedIn = true;
      this.loadData();
    }
  },
  beforeDestroy() {
    if (this.crawlerInterval) clearInterval(this.crawlerInterval);
  },
  watch: {
    videoPage() { this.fetchVideos(); },
    tab(val) {
      if (val === 'crawler') this.startPolling();
      else this.stopPolling();
    }
  },
  methods: {
    async login() {
      try {
        const { data } = await this.$http.post('/admin/login', {
          username: this.username,
          password: this.password
        });
        if (data.success) {
          localStorage.setItem('adminToken', data.token);
          this.isLoggedIn = true;
          this.loadData();
        }
      } catch (e) {
        this.error = '登录失败';
      }
    },
    logout() {
      localStorage.removeItem('adminToken');
      this.isLoggedIn = false;
    },
    async loadData() {
      await this.fetchStats();
      await this.fetchVideos();
      await this.fetchCategories();
      await this.fetchUsers();
    },
    async fetchStats() {
      try {
        const { data } = await this.$http.get('/admin/stats');
        this.stats = data;
      } catch (e) { void e; }
    },
    async fetchVideos() {
      try {
        const { data } = await this.$http.get('/admin/videos', {
          params: { page: this.videoPage, search: this.videoSearch, category: this.videoCategory }
        });
        this.videos = data.videos;
        this.videoPages = data.pages;
      } catch (e) { void e; }
    },
    async fetchCategories() {
      try {
        const { data } = await this.$http.get('/admin/categories');
        this.categories = data;
      } catch (e) { void e; }
    },
    async fetchUsers() {
      try {
        const { data } = await this.$http.get('/admin/users');
        this.users = data;
      } catch (e) { void e; }
    },
    async fetchPayments() {
      try {
        const { data } = await this.$http.get('/admin/payments', {
          params: { page: this.paymentPage }
        });
        this.payments = data.payments;
        this.paymentPages = data.pages;
      } catch (e) { void e; }
    },
    getCoverUrl(url) {
      if (!url) return 'https://dummyimage.com/60x40/333/fff.png&text=No';
      if (url.startsWith('/covers/')) return url;
      return url;
    },
    formatDate(d) {
      if (!d) return '-';
      return new Date(d).toLocaleDateString();
    },
    formatDateTime(d) {
      if (!d) return '-';
      return new Date(d).toLocaleString();
    },
    getPaymentType(type) {
      const types = { single: '单片', day: '包天', week: '包周', month: '包月', season: '包季', year: '包年', forever: '永久', hour: '包时' };
      return types[type] || type;
    },
    toggleAll(e) {
      this.selectedVideos = e.target.checked ? this.videos.map(v => v.id) : [];
    },
    editVideo(video) {
      this.editType = 'video';
      this.editItem = { ...video };
      this.showModal = true;
    },
    editUser(user) {
      this.editType = 'user';
      this.editItem = { ...user };
      this.showModal = true;
    },
    async saveEdit() {
      try {
        if (this.editType === 'video') {
          await this.$http.put(`/admin/videos/${this.editItem.id}`, this.editItem);
          this.fetchVideos();
        } else {
          await this.$http.put(`/admin/users/${this.editItem.id}`, this.editItem);
          this.fetchUsers();
        }
        this.showModal = false;
      } catch (e) {
        alert('保存失败');
      }
    },
    async deleteVideo(id) {
      if (!confirm('确定删除?')) return;
      await this.$http.delete(`/admin/videos/${id}`);
      this.fetchVideos();
      this.fetchStats();
    },
    async batchDelete() {
      if (!confirm(`确定删除 ${this.selectedVideos.length} 个视频?`)) return;
      await this.$http.post('/admin/videos/batch-delete', { ids: this.selectedVideos });
      this.selectedVideos = [];
      this.fetchVideos();
      this.fetchStats();
    },
    async deleteUser(id) {
      if (!confirm('确定删除?')) return;
      await this.$http.delete(`/admin/users/${id}`);
      this.fetchUsers();
      this.fetchStats();
    },
    async startCrawler() {
      try {
        await this.$http.post('/admin/crawler/start', this.crawlerConfig);
        this.startPolling();
      } catch (e) {
        alert('启动失败: ' + (e.response?.data?.error || e.message));
      }
    },
    async stopCrawler() {
      try {
        await this.$http.post('/admin/crawler/stop');
      } catch (e) { void e; }
    },
    startPolling() {
      if (this.crawlerInterval) return;
      this.crawlerInterval = setInterval(async () => {
        try {
          const { data } = await this.$http.get('/admin/crawler/status');
          this.crawlerStatus = data;
          this.$nextTick(() => {
            if (this.$refs.logBox) this.$refs.logBox.scrollTop = this.$refs.logBox.scrollHeight;
          });
        } catch (e) { void e; }
      }, 2000);
    },
    stopPolling() {
      if (this.crawlerInterval) {
        clearInterval(this.crawlerInterval);
        this.crawlerInterval = null;
      }
    },
    async fetchPricing() {
      try {
        const { data } = await this.$http.get('/admin/pricing');
        this.pricingConfigs = data;
      } catch (e) { 
        console.error('Failed to fetch pricing:', e);
      }
    },
    async savePricing(item) {
      try {
        await this.$http.put(`/admin/pricing/${item.id}`, {
          name: item.name,
          price: item.price,
          enabled: item.enabled
        });
        alert('保存成功!');
      } catch (e) {
        alert('保存失败: ' + (e.response?.data?.error || e.message));
      }
    },
    async saveAllPricing() {
      try {
        const configs = this.pricingConfigs.map(item => ({
          id: item.id,
          price: item.price,
          enabled: item.enabled
        }));
        await this.$http.post('/admin/pricing/batch-update', { configs });
        alert('全部保存成功!');
      } catch (e) {
        alert('保存失败: ' + (e.response?.data?.error || e.message));
      }
    },
    // ===== Promoters =====
    async fetchPromoters() {
      try {
        const { data } = await this.$http.get('/admin/promoters');
        this.promoters = data;
      } catch (e) { 
        console.error('Failed to fetch promoters:', e);
      }
    },
    async addPromoter() {
      if (!this.newPromoter.promoterId || !this.newPromoter.name) {
        alert('请填写推广ID和名称');
        return;
      }
      try {
        await this.$http.post('/admin/promoters', this.newPromoter);
        this.newPromoter = { promoterId: '', name: '', commissionRate: 10, username: '', password: '', customDomain: '' };
        this.fetchPromoters();
        alert('添加成功');
      } catch (e) {
        alert('添加失败: ' + (e.response?.data?.error || e.message));
      }
    },
    async updatePromoter(item) {
      try {
        await this.$http.put(`/admin/promoters/${item.id}`, {
          name: item.name,
          commissionRate: item.commissionRate,
          enabled: item.enabled
        });
      } catch (e) {
        alert('更新失败: ' + (e.response?.data?.error || e.message));
        this.fetchPromoters();
      }
    },
    async deletePromoter(id) {
      if (!confirm('确认删除此推广人员？')) return;
      try {
        await this.$http.delete(`/admin/promoters/${id}`);
        this.fetchPromoters();
      } catch (e) {
        alert('删除失败: ' + (e.response?.data?.error || e.message));
      }
    },
    async viewPromoterStats(promoterId) {
      try {
        const { data } = await this.$http.get(`/admin/promoters/${promoterId}/stats`);
        this.currentPromoterStats = data;
        this.showPromoterStats = true;
      } catch (e) {
        alert('获取统计失败: ' + (e.response?.data?.error || e.message));
      }
    },
    copyPromoLink(promoterId) {
      const link = `${window.location.origin}/?ref=${promoterId}`;
      navigator.clipboard.writeText(link).then(() => {
        alert('推广链接已复制: ' + link);
      }).catch(() => {
        prompt('请复制推广链接:', link);
      });
    },
    // ===== Site Statistics =====
    async fetchSiteStats() {
      try {
        const { data } = await this.$http.get('/admin/site-stats');
        this.siteStats = data;
      } catch (e) {
        console.error('Failed to fetch site stats:', e);
      }
    },
    // ===== Site Settings =====
    async fetchSettings() {
      try {
        const { data } = await this.$http.get('/settings');
        this.siteSettings = { ...this.siteSettings, ...data };
      } catch (e) { 
        console.error('Failed to fetch settings:', e);
      }
    },
    async saveSettings() {
      try {
        await this.$http.put('/admin/settings', {
          settings: this.siteSettings
        });
        alert('设置已保存！刷新页面即可看到效果。');
      } catch (e) {
        alert('保存失败: ' + (e.response?.data?.error || e.message));
      }
    },
    // ===== System Info =====
    async fetchVersionInfo() {
      try {
        const { data } = await this.$http.get('/version');
        this.versionInfo.backend = data.backend;
      } catch (e) {
        console.error('Failed to fetch version:', e);
      }
    },
    async changeAdminPassword() {
      const { currentPassword, newPassword, confirmPassword } = this.passwordForm;
      if (!currentPassword || !newPassword) {
        alert('请填写当前密码和新密码');
        return;
      }
      if (newPassword !== confirmPassword) {
        alert('两次输入的新密码不一致');
        return;
      }
      if (newPassword.length < 6) {
        alert('新密码至少6位');
        return;
      }
      try {
        const { data } = await this.$http.post('/admin/change-password', {
          currentPassword,
          newPassword
        });
        alert(data.message || '密码修改成功');
        this.passwordForm = { currentPassword: '', newPassword: '', confirmPassword: '' };
      } catch (e) {
        alert('修改失败: ' + (e.response?.data?.error || e.message));
      }
    }
  }
}
</script>

<style scoped>
.admin-container { min-height: 100vh; background: #1a1a2e; color: #fff; }
.login-box { max-width: 300px; margin: 100px auto; padding: 30px; background: #16213e; border-radius: 8px; text-align: center; }
.login-box h2 { margin-bottom: 20px; color: #e94560; }
.login-box input { width: 100%; padding: 10px; margin: 8px 0; border: none; border-radius: 4px; }
.login-box button { width: 100%; padding: 12px; background: #e94560; color: #fff; border: none; border-radius: 4px; cursor: pointer; margin-top: 10px; }
.error { color: #ff6b6b; margin-top: 10px; }

.admin-panel { display: flex; min-height: 100vh; }
.sidebar { width: 200px; background: #16213e; padding: 20px; }
.sidebar h3 { color: #e94560; margin-bottom: 20px; }
.menu-item { padding: 12px; cursor: pointer; border-radius: 4px; margin-bottom: 8px; }
.menu-item:hover, .menu-item.active { background: #e94560; }
.menu-item.logout { margin-top: 50px; background: #333; }

.content { flex: 1; padding: 20px; overflow-x: auto; }
.section h2 { color: #e94560; margin-bottom: 20px; }

.stat-cards { display: flex; gap: 20px; flex-wrap: wrap; }
.stat-card { background: #16213e; padding: 30px; border-radius: 8px; text-align: center; min-width: 150px; }
.stat-card.revenue { background: #2d5016; }
.stat-num { font-size: 36px; font-weight: bold; color: #e94560; }
.stat-card.revenue .stat-num { color: #4CAF50; }

.toolbar { display: flex; gap: 10px; margin-bottom: 15px; }
.toolbar input, .toolbar select { padding: 8px; border: none; border-radius: 4px; background: #16213e; color: #fff; }

table { width: 100%; border-collapse: collapse; background: #16213e; border-radius: 8px; overflow: hidden; }
th, td { padding: 12px; text-align: left; border-bottom: 1px solid #333; }
th { background: #0f3460; }
.cover-thumb { width: 60px; height: 40px; object-fit: cover; border-radius: 4px; }
.title-cell { max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.amount { color: #4CAF50; font-weight: bold; }
.status-completed { color: #4CAF50; }
.status-pending { color: #ff9800; }
.status-failed { color: #f44336; }

.btn-sm { padding: 5px 10px; border: none; border-radius: 4px; cursor: pointer; margin-right: 5px; background: #0f3460; color: #fff; }
.btn-danger { background: #e94560; color: #fff; border: none; padding: 8px 15px; border-radius: 4px; cursor: pointer; }
.btn-primary { background: #4CAF50; color: #fff; border: none; padding: 10px 20px; border-radius: 4px; cursor: pointer; }

.pagination { margin-top: 15px; display: flex; gap: 10px; align-items: center; }
.pagination button { padding: 8px 15px; background: #0f3460; color: #fff; border: none; border-radius: 4px; cursor: pointer; }
.pagination button:disabled { opacity: 0.5; cursor: not-allowed; }

.crawler-controls { background: #16213e; padding: 20px; border-radius: 8px; margin-bottom: 20px; }
.control-row { display: flex; align-items: center; gap: 15px; margin-bottom: 15px; }
.control-row input { padding: 8px; width: 80px; border: none; border-radius: 4px; }
.status-bar { display: flex; gap: 30px; padding: 10px; background: #0f3460; border-radius: 4px; }
.status-running { color: #4CAF50; }

.log-box { background: #16213e; padding: 15px; border-radius: 8px; }
.log-box pre { background: #000; padding: 15px; border-radius: 4px; max-height: 400px; overflow: auto; font-size: 12px; white-space: pre-wrap; }

.modal-mask { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.7); display: flex; align-items: center; justify-content: center; z-index: 100; }
.modal-box { background: #16213e; padding: 25px; border-radius: 8px; min-width: 400px; max-width: 600px; }
.modal-box h3 { margin-bottom: 20px; color: #e94560; }
.modal-box label { display: block; margin: 10px 0 5px; color: #aaa; }
.modal-box input, .modal-box textarea { width: 100%; padding: 10px; border: none; border-radius: 4px; background: #0f3460; color: #fff; box-sizing: border-box; }
.modal-box textarea { resize: vertical; font-family: monospace; font-size: 12px; }
.cover-preview { margin-top: 10px; }
.cover-preview img { max-width: 200px; border-radius: 4px; }
.modal-actions { display: flex; gap: 10px; margin-top: 20px; justify-content: flex-end; }
.modal-actions button { padding: 10px 20px; border: none; border-radius: 4px; cursor: pointer; }

/* Pricing Management Styles */
.pricing-tip { color: #aaa; margin-bottom: 20px; font-size: 14px; }
.pricing-actions { margin-top: 20px; display: flex; justify-content: flex-end; }
.price-input { width: 80px; padding: 8px; background: #0f3460; border: 1px solid #333; border-radius: 4px; color: #fff; text-align: center; }
.price-input:focus { border-color: #e94560; outline: none; }
.type-badge { background: #0f3460; padding: 4px 8px; border-radius: 4px; font-size: 12px; font-family: monospace; }
.badge-member { background: #4CAF50; color: #fff; padding: 4px 8px; border-radius: 4px; font-size: 12px; }
.badge-single { background: #ff9800; color: #fff; padding: 4px 8px; border-radius: 4px; font-size: 12px; }
.disabled-row { opacity: 0.5; }

/* Toggle Switch */
.switch { position: relative; display: inline-block; width: 50px; height: 26px; }
.switch input { opacity: 0; width: 0; height: 0; }
.slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #333; transition: .3s; border-radius: 26px; }
.slider:before { position: absolute; content: ""; height: 20px; width: 20px; left: 3px; bottom: 3px; background-color: white; transition: .3s; border-radius: 50%; }
input:checked + .slider { background-color: #4CAF50; }
input:checked + .slider:before { transform: translateX(24px); }

/* Promoters Management Styles */
.add-promoter-form { background: #16213e; padding: 20px; border-radius: 8px; margin-bottom: 20px; }
.add-promoter-form h4 { margin-bottom: 15px; color: #e94560; }
.form-row { display: flex; gap: 10px; flex-wrap: wrap; }
.form-row input { flex: 1; min-width: 150px; padding: 10px; border: none; border-radius: 4px; background: #0f3460; color: #fff; }
.form-row input:focus { outline: 2px solid #e94560; }
.btn-sm { padding: 6px 12px; border: none; border-radius: 4px; cursor: pointer; background: #0f3460; color: #fff; margin-right: 5px; }
.btn-sm:hover { background: #e94560; }
.btn-danger { background: #e74c3c !important; }
.btn-danger:hover { background: #c0392b !important; }
.stats-panel { background: #16213e; padding: 20px; border-radius: 8px; margin-top: 20px; border: 2px solid #e94560; }
.stats-panel h4 { margin-bottom: 20px; color: #e94560; }
.stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 15px; margin-bottom: 20px; }
.stat-box { background: #0f3460; padding: 15px; border-radius: 8px; text-align: center; }
.stat-value { font-size: 24px; font-weight: bold; color: #fff; }
.stat-label { font-size: 12px; color: #aaa; margin-top: 5px; }
.stat-box.highlight { background: #e94560; }
.stat-box.highlight-green { background: #27ae60; }

/* Site Settings Styles */
.settings-form { background: #16213e; padding: 30px; border-radius: 8px; max-width: 600px; }
.setting-item { margin-bottom: 20px; }
.setting-item label { display: block; margin-bottom: 8px; color: #aaa; font-size: 14px; }
.setting-item input { width: 100%; padding: 12px; border: none; border-radius: 4px; background: #0f3460; color: #fff; box-sizing: border-box; font-size: 16px; }
.setting-item input:focus { outline: 2px solid #e94560; }
.settings-actions { margin-top: 30px; }
.settings-tip { margin-top: 20px; color: #aaa; font-size: 14px; }
.settings-tip p { background: #16213e; padding: 15px; border-radius: 4px; border-left: 4px solid #e94560; }

/* Version Info Styles */
.version-info { background: #0f3460; padding: 20px; border-radius: 8px; }
.version-item { display: flex; justify-content: space-between; align-items: center; padding: 12px 0; border-bottom: 1px solid #333; }
.version-item:last-child { border-bottom: none; }
.version-label { color: #aaa; font-size: 14px; }
.version-value { color: #4CAF50; font-size: 18px; font-weight: bold; font-family: monospace; }

/* Site Statistics Styles */
.site-stats-section { background: #16213e; padding: 20px; border-radius: 8px; margin-top: 30px; border: 2px solid #4CAF50; }
.site-stats-section h3 { margin-bottom: 20px; color: #4CAF50; }
.site-stats-section h4 { margin-bottom: 15px; color: #e94560; font-size: 14px; }
.stats-detail-section { background: #0f3460; padding: 15px; border-radius: 8px; margin-bottom: 15px; }
.device-stats { display: flex; gap: 20px; flex-wrap: wrap; }
.device-item { display: flex; align-items: center; gap: 10px; background: #16213e; padding: 10px 15px; border-radius: 6px; }
.device-icon { font-size: 20px; }
.device-name { color: #aaa; }
.device-count { color: #4CAF50; font-weight: bold; font-size: 18px; }
.mini-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.mini-table th, .mini-table td { padding: 8px 12px; text-align: left; border-bottom: 1px solid #333; }
.mini-table th { background: #16213e; color: #aaa; font-weight: normal; }
.mini-table td { color: #fff; }
.url-cell { max-width: 250px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 12px; }
.top-tables-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 15px; }
</style>
